import { Link } from "react-router-dom";
import { FileText, Download, CheckCircle, AlertCircle, Clock, Shield, ChevronRight } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from '../components/SEO';

const SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'HowTo',
  'name': 'Mobbing Protokoll führen: Kostenlose Vorlage und Anleitung',
  'description': 'Kostenlose Mobbing-Protokoll-Vorlage für Arbeitnehmer und Schüler. Schritt-für-Schritt-Anleitung, was ins Protokoll muss und wie du es richtig führst.',
  'step': [
    { '@type': 'HowToStep', 'name': 'Protokoll anlegen', 'text': 'Lege ein eigenes Dokument oder Notizbuch an, das nur für dein Mobbing-Protokoll bestimmt ist.' },
    { '@type': 'HowToStep', 'name': 'Ersten Vorfall eintragen', 'text': 'Trage den ersten Vorfall mit Datum, Uhrzeit, Ort, Beteiligten und exaktem Wortlaut ein.' },
    { '@type': 'HowToStep', 'name': 'Beweise anhängen', 'text': 'Füge Screenshots, Fotos oder andere Beweise als Anhang bei.' },
    { '@type': 'HowToStep', 'name': 'Konsequent weiterführen', 'text': 'Trage jeden weiteren Vorfall sofort ein — auch wenn er "klein" erscheint.' },
    { '@type': 'HowToStep', 'name': 'Protokoll sichern', 'text': 'Speichere das Protokoll an einem sicheren Ort, auf den der Mobber keinen Zugriff hat.' },
  ]
};

const TEMPLATE_FIELDS = [
  { label: "Datum & Uhrzeit", example: "z. B. 15.04.2025, 09:30 Uhr", important: true },
  { label: "Ort", example: "z. B. Büro Raum 204, Schulhof, WhatsApp-Gruppe", important: true },
  { label: "Beteiligte Personen", example: "Name + Rolle (Kollege, Vorgesetzter, Mitschüler)", important: true },
  { label: "Was wurde gesagt/getan?", example: "Wörtliche Wiedergabe soweit möglich", important: true },
  { label: "Zeugen", example: "Name + ob sie bereit sind auszusagen", important: false },
  { label: "Auswirkungen auf mich", example: "Schlafprobleme, Magenschmerzen, Angst, …", important: false },
  { label: "Anhänge/Beweise", example: "Screenshot, Foto, E-Mail-Anhang", important: false },
  { label: "Meine Reaktion", example: "Was habe ich gesagt/getan? Habe ich mich beschwert?", important: false },
];

const TIPS = [
  {
    icon: Clock,
    color: "#A0E8AF",
    title: "Sofort aufschreiben",
    text: "Nicht auf morgen warten. Erinnerungen verblassen — ein direkt geschriebener Eintrag ist glaubwürdiger."
  },
  {
    icon: Shield,
    color: "#BDE0FE",
    title: "Sicher aufbewahren",
    text: "Das Protokoll gehört nicht auf den Firmen-PC oder das Schul-iPad. Nutze dein privates Gerät oder Cloud-Speicher."
  },
  {
    icon: CheckCircle,
    color: "#FFC4D6",
    title: "Alles dokumentieren",
    text: "Auch \"kleine\" Vorfälle eintragen. Erst die Summe zeigt das Muster — und das ist oft entscheidend."
  },
  {
    icon: AlertCircle,
    color: "#F6E05E",
    title: "Keine Übertreibungen",
    text: "Bleib sachlich und exakt. Ein glaubwürdiges Protokoll ist wertvoller als ein übertriebenes."
  },
];

export default function MobbingProtokoll() {
  return (
    <div className="min-h-screen bg-[#FFFDF9]">
      <Header />
      <SEO
        title="Mobbing Protokoll: Kostenlose Vorlage + Tool 2025"
        description="Kostenlose Mobbing-Protokoll-Vorlage: Was muss rein, wie führst du es richtig? Anleitung für Arbeitnehmer und Schüler + anonymes Online-Tool."
        path="/mobbing-protokoll"
        schema={SCHEMA}
      />

      <main className="px-6 md:px-12 lg:px-24 py-12">
        <div className="max-w-4xl mx-auto">

          {/* Hero */}
          <div className="text-center mb-12">
            <div className="w-20 h-20 bg-[#BDE0FE]/30 rounded-full flex items-center justify-center mx-auto mb-6">
              <Download className="w-10 h-10 text-[#1A365D]" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-[#2D3748] mb-4" style={{ fontFamily: 'Nunito' }}>
              Mobbing Protokoll: Kostenlose Vorlage & Anleitung
            </h1>
            <p className="text-lg text-[#718096] max-w-2xl mx-auto">
              Ein lückenloses Mobbing-Protokoll ist dein stärkstes Mittel. Hier bekommst du die Vorlage, die Anleitung — und ein kostenloses Online-Tool, das dir automatisch ein PDF erstellt.
            </p>
          </div>

          {/* Quick CTA */}
          <div className="bg-[#A0E8AF]/20 border border-[#A0E8AF] rounded-2xl p-6 mb-10 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div>
              <p className="font-bold text-[#1A4731] mb-1">Kein Bock auf Word-Vorlagen?</p>
              <p className="text-[#4A5568] text-sm">Nutze unser Online-Tool: Formular ausfüllen → strukturiertes PDF-Protokoll automatisch erstellen.</p>
            </div>
            <Link
              to="/melden"
              className="inline-flex items-center gap-2 bg-[#A0E8AF] text-[#1A4731] font-bold px-6 py-3 rounded-full hover:bg-[#86D696] transition-all whitespace-nowrap"
            >
              Online-Protokoll erstellen
              <ChevronRight className="w-4 h-4" />
            </Link>
          </div>

          {/* Template */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-[#2D3748] mb-2" style={{ fontFamily: 'Nunito' }}>
              Die Vorlage: Was ins Protokoll muss
            </h2>
            <p className="text-[#718096] mb-6 text-sm">
              Für jeden Vorfall einen eigenen Eintrag anlegen. <span className="font-semibold text-[#2D3748]">Fettgedruckte Felder</span> sind Pflicht.
            </p>

            <div className="bg-white border-2 border-dashed border-gray-200 rounded-2xl overflow-hidden">
              <div className="bg-[#2D3748] text-white px-6 py-3 text-sm font-bold">
                MOBBING-PROTOKOLL — Eintrag [Nummer] — [Datum]
              </div>
              <div className="divide-y divide-gray-100">
                {TEMPLATE_FIELDS.map((field, i) => (
                  <div key={i} className="flex px-6 py-4 gap-4">
                    <div className="w-44 flex-shrink-0">
                      <span className={`text-sm ${field.important ? 'font-bold text-[#2D3748]' : 'text-[#718096]'}`}>
                        {field.label}
                        {field.important && <span className="text-red-400 ml-1">*</span>}
                      </span>
                    </div>
                    <div className="flex-1 bg-gray-50 rounded-lg px-4 py-2 text-sm text-[#A0AEC0] italic">
                      {field.example}
                    </div>
                  </div>
                ))}
              </div>
              <div className="px-6 py-3 bg-gray-50 text-xs text-[#A0AEC0]">
                * Pflichtfelder — Beweise als separate Dateien aufbewahren und referenzieren
              </div>
            </div>
          </div>

          {/* Tips */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-[#2D3748] mb-6" style={{ fontFamily: 'Nunito' }}>
              4 Regeln für ein wirksames Protokoll
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              {TIPS.map((tip, i) => {
                const Icon = tip.icon;
                return (
                  <div key={i} className="bg-white rounded-2xl border border-gray-100 p-5 flex gap-4">
                    <div
                      className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: `${tip.color}30` }}
                    >
                      <Icon className="w-5 h-5" style={{ color: tip.color }} />
                    </div>
                    <div>
                      <h3 className="font-bold text-[#2D3748] mb-1 text-sm">{tip.title}</h3>
                      <p className="text-[#718096] text-sm">{tip.text}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Who needs it */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-[#2D3748] mb-6" style={{ fontFamily: 'Nunito' }}>
              Wer braucht ein Mobbing-Protokoll?
            </h2>
            <div className="grid md:grid-cols-3 gap-4">
              {[
                { emoji: "👔", title: "Arbeitnehmer", text: "Für Betriebsrat, Anwalt, Arbeitsamt oder als Grundlage für eine Klage." },
                { emoji: "🎒", title: "Schüler & Eltern", text: "Für Schulleitung, Schulamt oder wenn Cybermobbing angezeigt werden soll." },
                { emoji: "🏫", title: "Lehrer & Pädagogen", text: "Als strukturierte Basis für Gespräche mit Eltern und Schulbehörden." },
              ].map((item, i) => (
                <div key={i} className="bg-white rounded-2xl border border-gray-100 p-5 text-center">
                  <div className="text-4xl mb-3">{item.emoji}</div>
                  <h3 className="font-bold text-[#2D3748] mb-2">{item.title}</h3>
                  <p className="text-[#718096] text-sm">{item.text}</p>
                </div>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="bg-gradient-to-br from-[#EBF8FF] to-[#E6FFFA] rounded-2xl p-8 text-center">
            <h2 className="text-2xl font-bold text-[#2D3748] mb-3" style={{ fontFamily: 'Nunito' }}>
              Protokoll jetzt online erstellen — kostenlos & anonym
            </h2>
            <p className="text-[#718096] mb-6 max-w-xl mx-auto">
              Kein Download, keine Registrierung. Einfach Formular ausfüllen und ein strukturiertes PDF-Protokoll erhalten.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/melden"
                className="inline-flex items-center justify-center gap-2 bg-[#A0E8AF] text-[#1A4731] font-bold px-8 py-4 rounded-full hover:bg-[#86D696] transition-all"
              >
                <FileText className="w-5 h-5" />
                Protokoll erstellen
              </Link>
              <Link
                to="/mobbing-dokumentieren"
                className="inline-flex items-center justify-center gap-2 border-2 border-[#A0E8AF] text-[#1A4731] font-bold px-8 py-4 rounded-full hover:bg-[#A0E8AF]/20 transition-all"
              >
                Beweise richtig sichern →
              </Link>
            </div>
          </div>

        </div>
      </main>
      <Footer />
    </div>
  );
}
